import React, { useState, useEffect } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Bell, Send, CheckCircle2, XCircle, RefreshCw, ShieldCheck, ShieldAlert, ShieldX, ExternalLink, Clock, Zap } from "lucide-react";

export default function AdminEmails() {
  const [days, setDays] = useState(7);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testRecipient, setTestRecipient] = useState("parcavenuegroup@gmail.com");
  const [domainStatus, setDomainStatus] = useState(null);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [schedStatus, setSchedStatus] = useState(null);
  const [triggering, setTriggering] = useState(false);

  const checkDomain = async () => {
    setCheckingDomain(true);
    try {
      const { data } = await api.get("/admin/email/domain-status");
      setDomainStatus(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCheckingDomain(false); }
  };

  const loadScheduler = async () => {
    try {
      const { data } = await api.get("/admin/scheduler/status");
      setSchedStatus(data);
    } catch (e) { /* silent */ }
  };

  const triggerJob = async (jobId) => {
    setTriggering(true);
    try {
      const { data } = await api.post(`/admin/scheduler/trigger/${jobId}`);
      toast.success(`Job ${jobId} exécuté`);
      setSchedStatus(data.status);
      // Aussi rafraîchir le résumé de rappels
      if (jobId === "renewal_reminders" && data.last_run?.result) {
        setLastResult(data.last_run.result);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTriggering(false); loadScheduler(); }
  };

  useEffect(() => { checkDomain(); loadScheduler(); }, []);

  const sendReminders = async () => {
    setRunning(true);
    setLastResult(null);
    try {
      const { data } = await api.post(`/admin/email/renewal-reminders?days=${days}`);
      setLastResult(data);
      if (data.sent > 0) toast.success(`${data.sent} rappels envoyés`);
      else toast.message("Aucun rappel envoyé (aucune entreprise éligible dans la fenêtre)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setRunning(false); }
  };

  const sendTestEmail = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/email/test", { to: testRecipient || null });
      setTestResult(data);
      if (data.ok) toast.success(`Email de test envoyé à ${data.recipient || testRecipient}`);
      else toast.error(data.error || data.reason);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setTestResult({ ok: false, error: e.response?.data?.detail });
    } finally { setTesting(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl" data-testid="admin-emails-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-[#868E96] font-bold mb-2">// Emails transactionnels</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Resend & Notifications</h1>
        <p className="text-sm text-[#495057] mt-2">
          Vérifiez le domaine expéditeur, testez les envois et pilotez les rappels de renouvellement.
        </p>
      </div>

      {/* Expéditeur & délivrabilité (Phase 39) */}
      <SenderCard />

      {/* Domain status */}
      <DomainStatusCard status={domainStatus} loading={checkingDomain} onRefresh={checkDomain} />

      {/* Scheduler status */}
      <SchedulerCard status={schedStatus} triggering={triggering} onTrigger={triggerJob} onRefresh={loadScheduler} />

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {/* Renewal reminders */}
        <div className="border border-[#DEE2E6] bg-white p-6" data-testid="renewal-reminders-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 bg-[#FFD700] flex items-center justify-center">
              <Bell className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-black">Rappels renouvellement</h2>
              <p className="text-xs text-[#495057] mt-1">Envoie un rappel aux entreprises dont l&apos;échéance approche.</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Fenêtre (jours avant échéance)</Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value) || 7)}
                data-testid="reminder-days"
                className="h-10 mt-1 max-w-[120px]"
              />
              <div className="text-[10px] text-[#868E96] mt-1">Cible entreprises avec due_date à ±1 jour de {days}j.</div>
            </div>
            <Button
              onClick={sendReminders}
              disabled={running}
              data-testid="send-reminders-btn"
              className="w-full h-11 bg-[#002FA7] text-white"
            >
              {running ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Envoi…</> : <><Send className="h-4 w-4 mr-2" /> Envoyer les rappels</>}
            </Button>
          </div>

          {lastResult && (
            <div className="mt-4 border-t border-[#DEE2E6] pt-4 text-sm" data-testid="reminders-result">
              <div className="flex items-center gap-4 mb-2">
                <StatBadge label="Envoyés" value={lastResult.sent} color="#059669" />
                <StatBadge label="Ignorés" value={lastResult.skipped} color="#868E96" />
                <StatBadge label="Erreurs" value={lastResult.errors} color="#E60000" />
              </div>
              {lastResult.details?.sent?.length > 0 && (
                <div className="text-xs text-[#495057] mt-2">
                  <div className="font-semibold mb-1">Envoyés :</div>
                  <ul className="space-y-0.5">
                    {lastResult.details.sent.map((s, i) => (
                      <li key={i} className="font-mono">{s.code} · {s.email} · {s.days_left}j</li>
                    ))}
                  </ul>
                </div>
              )}
              {lastResult.details?.errors?.length > 0 && (
                <div className="text-xs text-[#E60000] mt-2">
                  <div className="font-semibold mb-1">Erreurs :</div>
                  <ul className="space-y-0.5">
                    {lastResult.details.errors.map((s, i) => (
                      <li key={i} className="font-mono">{s.code} · {s.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Test email */}
        <div className="border border-[#DEE2E6] bg-white p-6" data-testid="test-email-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 bg-[#002FA7] text-white flex items-center justify-center">
              <Mail className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-black">Config Resend</h2>
              <p className="text-xs text-[#495057] mt-1">Envoie un email test à l&apos;adresse indiquée pour vérifier la config.</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Destinataire du test</Label>
              <Input
                type="email"
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                data-testid="test-recipient"
                placeholder="parcavenuegroup@gmail.com"
                className="h-10 mt-1"
              />
              <div className="text-[10px] text-[#868E96] mt-1">
                ⚠️ Tant que le domaine n&apos;est pas vérifié, Resend n&apos;autorise l&apos;envoi qu&apos;à l&apos;adresse du compte Resend.
              </div>
            </div>
            <Button
              onClick={sendTestEmail}
              disabled={testing || !testRecipient}
              data-testid="test-email-btn"
              variant="outline"
              className="w-full h-11 border-2 border-[#002FA7] text-[#002FA7]"
            >
              {testing ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Envoi…</> : <><Send className="h-4 w-4 mr-2" /> Envoyer un email de test</>}
            </Button>
          </div>
          {testResult && (
            <div className={`mt-4 border-t border-[#DEE2E6] pt-4 flex items-center gap-2 text-sm ${testResult.ok ? "text-[#059669]" : "text-[#E60000]"}`} data-testid="test-email-result">
              {testResult.ok ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
              <div>
                {testResult.ok ? (
                  <>Email envoyé · <span className="font-mono text-xs">{testResult.id}</span></>
                ) : (
                  <>Erreur : {testResult.error || testResult.reason}</>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 text-xs text-[#868E96]">
            <div className="font-semibold mb-1">Emails envoyés automatiquement :</div>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>Validation inscription (avec lien Stripe)</li>
              <li>Livraison identifiants après paiement</li>
              <li>Accusé de résiliation reçue</li>
              <li>Confirmation résiliation traitée</li>
              <li>Rappel renouvellement (manuel via ce panel)</li>
              <li>Magic-link parent (messagerie éducation)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function SenderCard() {
  const [data, setData] = useState(null);
  const [dns, setDns] = useState(null);
  const [deliv, setDeliv] = useState(null);
  const [form, setForm] = useState({ sender_email: "", sender_name: "", reply_to: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/email/sender");
      setData(d);
      setForm({ sender_email: d.email || "", sender_name: d.name || "", reply_to: d.reply_to || "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadDeliv = async () => {
    try {
      const { data: d } = await api.get("/admin/email/deliverability");
      setDeliv(d);
    } catch (e) { /* silencieux */ }
  };

  const loadDns = async () => {
    try {
      const { data: d } = await api.get("/admin/email/dns-records");
      setDns(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); loadDeliv(); loadDns(); }, []);

  const save = async (force = false) => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/admin/email/sender", { ...form, force });
      toast.success(`Expéditeur enregistré : ${d.email}`);
      await load();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const autofix = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.post("/admin/email/sender/autofix");
      toast.success(`Expéditeur corrigé : ${d.before} → ${d.after}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const aligned = data?.aligned;

  return (
    <div className="border-2 bg-white p-6 mb-6" data-testid="sender-card"
         style={{ borderColor: aligned === true ? "#059669" : aligned === false ? "#E60000" : "#DEE2E6" }}>
      <div className="flex items-start gap-3 mb-4">
        {aligned === true ? <ShieldCheck className="h-6 w-6 text-[#059669]" />
          : aligned === false ? <ShieldX className="h-6 w-6 text-[#E60000]" />
          : <ShieldAlert className="h-6 w-6 text-[#F59E0B]" />}
        <div className="flex-1">
          <h2 className="font-display text-xl font-bold tracking-tight">Expéditeur & délivrabilité</h2>
          <p className="text-xs text-[#495057]">
            L'adresse d'envoi doit appartenir à un domaine <b>vérifié</b> (SPF + DKIM) : c'est la condition n°1
            pour ne pas tomber dans les spams.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="sender-reload"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {loading ? <div className="text-xs text-[#868E96]">Chargement…</div> : (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <div className="p-3 bg-[#F8F9FA] border border-[#DEE2E6]">
              <div className="text-[10px] uppercase tracking-wider text-[#868E96] font-bold">Expéditeur actuel</div>
              <div className="font-mono text-sm break-all" data-testid="sender-current">{data?.name} &lt;{data?.email}&gt;</div>
            </div>
            <div className="p-3 bg-[#F8F9FA] border border-[#DEE2E6]">
              <div className="text-[10px] uppercase tracking-wider text-[#868E96] font-bold">Alignement SPF/DKIM</div>
              <div className={`text-sm font-bold ${aligned ? "text-[#059669]" : "text-[#E60000]"}`} data-testid="sender-aligned">
                {aligned === true ? "✓ Domaine vérifié" : aligned === false ? "✗ Domaine NON vérifié" : "Inconnu"}
              </div>
              <div className="text-[10px] text-[#868E96]">Vérifiés : {(data?.verified_domains || []).join(", ") || "aucun"}</div>
            </div>
            <div className="p-3 bg-[#F8F9FA] border border-[#DEE2E6]">
              <div className="text-[10px] uppercase tracking-wider text-[#868E96] font-bold">Anti-spam actif</div>
              <div className="text-[11px] text-[#495057]">
                Texte brut · From nommé · Reply-To · List-Unsubscribe
              </div>
            </div>
          </div>

          {data?.autofixed_from && (
            <div className="text-[11px] p-2 bg-[#FFFDF2] border-l-3 border-[#FFD700] mb-3" data-testid="sender-autofix-note">
              Corrigé automatiquement depuis <code>{data.autofixed_from}</code> (domaine non vérifié).
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Adresse d'envoi</Label>
              <Input value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                     data-testid="sender-email-input" placeholder="noreply@avenuerh.pro" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Nom affiché</Label>
              <Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                     data-testid="sender-name-input" placeholder="PAG Business Manager" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Répondre à</Label>
              <Input value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
                     data-testid="sender-replyto-input" placeholder="contact@parcavenue.eu" />
            </div>
          </div>

          {(data?.suggestions || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {data.suggestions.map((s) => (
                <button key={s} type="button" onClick={() => setForm({ ...form, sender_email: s })}
                        data-testid={`sender-suggest-${s}`}
                        className="text-[11px] font-mono px-2 py-1 border border-[#DEE2E6] bg-white hover:border-[#002FA7]">
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <Button onClick={() => save(false)} disabled={saving} className="bg-[#002FA7] text-white" data-testid="sender-save">
              {saving ? "Enregistrement…" : "Enregistrer l'expéditeur"}
            </Button>
            {aligned === false && (
              <Button onClick={autofix} disabled={saving} className="bg-[#059669] text-white" data-testid="sender-autofix">
                <ShieldCheck className="h-4 w-4 mr-1" />Corriger automatiquement
              </Button>
            )}
            <Button variant="outline" onClick={() => { loadDns(); loadDeliv(); }} data-testid="sender-dns">
              <RefreshCw className="h-4 w-4 mr-1" />Revérifier le DNS
            </Button>
          </div>

          {/* Action DMARC — bloc prioritaire tant que l'enregistrement manque */}
          {deliv?.missing?.includes("dmarc") && (
            <DmarcActionPanel fix={deliv.checks.find((c) => c.key === "dmarc")?.fix} />
          )}

          {/* Audit anti-spam */}
          {deliv && (
            <div className="mt-4 border-2 border-[#DEE2E6]" data-testid="deliverability-card">
              <div className="px-3 py-2 bg-[#0A0A0A] text-white flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold">Score anti-spam · {deliv.domain}</div>
                <div className="font-display font-black text-lg" data-testid="deliverability-score"
                     style={{ color: deliv.score >= 100 ? "#22C55E" : deliv.score >= 80 ? "#FFD700" : "#FF6B6B" }}>
                  {deliv.score}/100
                </div>
              </div>
              <div className="divide-y divide-[#DEE2E6]">
                {deliv.checks.map((c) => (
                  <div key={c.key} className="p-3" data-testid={`deliv-check-${c.key}`}>
                    <div className="flex items-start gap-2">
                      {c.ok ? <CheckCircle2 className="h-4 w-4 text-[#059669] mt-0.5" />
                            : <XCircle className="h-4 w-4 text-[#E60000] mt-0.5" />}
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{c.label}</div>
                        <div className="text-[11px] font-mono text-[#495057] break-all">{c.detail}</div>
                        {c.fix && (
                          <div className="mt-2 p-2 bg-[#FFF5F5] border-l-3 border-[#E60000]">
                            <div className="text-[11px] text-[#495057] mb-1">{c.fix.how}</div>
                            {c.fix.type && (
                              <div className="flex flex-wrap items-center gap-2">
                                <code className="text-[11px] bg-white border border-[#DEE2E6] px-2 py-1 break-all">
                                  {c.fix.type} · {c.fix.name} · {c.fix.value}
                                </code>
                                <Button size="sm" variant="outline" data-testid={`deliv-copy-${c.key}`}
                                        onClick={() => { navigator.clipboard.writeText(c.fix.value); toast.success("Valeur copiée"); }}>
                                  Copier
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {dns && (
            <div className="mt-4 border border-[#DEE2E6]" data-testid="dns-records">
              <div className="px-3 py-2 bg-[#F8F9FA] text-[10px] uppercase tracking-[0.2em] font-bold border-b border-[#DEE2E6]">
                DNS · {dns.domain} {dns.status ? `· ${dns.status}` : ""} {dns.error ? `· ${dns.error}` : ""}
              </div>
              <div className="divide-y divide-[#DEE2E6] max-h-64 overflow-y-auto">
                {(dns.records || []).map((r, i) => (
                  <div key={i} className="p-2 text-[11px] font-mono break-all" data-testid={`dns-record-${i}`}>
                    <b>{r.type}</b> · {r.name} · <span className="text-[#495057]">{r.value}</span>
                    {r.status && <span className={`ml-1 uppercase ${r.status === "verified" ? "text-[#059669]" : "text-[#F59E0B]"}`}>[{r.status}]</span>}
                  </div>
                ))}
                {dns.dmarc_suggestion && (
                  <div className="p-2 text-[11px] font-mono break-all bg-[#FFFDF2]" data-testid="dns-dmarc">
                    <b>{dns.dmarc_suggestion.type}</b> · {dns.dmarc_suggestion.name} · {dns.dmarc_suggestion.value}
                    <div className="font-sans text-[10px] text-[#495057] mt-0.5">{dns.dmarc_suggestion.note}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DmarcActionPanel({ fix }) {
  if (!fix) return null;
  const host = (fix.name || "").replace(/\.[^.]+\.[^.]+$/, "") || "_dmarc";
  const copy = (val, label) => { navigator.clipboard.writeText(val); toast.success(`${label} copié`); };
  const rows = [
    { label: "Type", value: fix.type || "TXT", testid: "type" },
    { label: "Nom / Host", value: host, testid: "host" },
    { label: "Valeur", value: fix.value, testid: "value" },
    { label: "TTL", value: "3600", testid: "ttl" },
  ];
  return (
    <div className="mt-4 border-2 border-[#E60000] bg-[#FFF5F5]" data-testid="dmarc-action-panel">
      <div className="px-4 py-2 bg-[#E60000] text-white flex items-center gap-2">
        <ShieldAlert className="h-4 w-4" />
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold">Action requise · DMARC manquant → vos emails partent en spam</div>
      </div>
      <div className="p-4">
        <div className="text-xs text-[#495057] mb-3">
          Publiez cet enregistrement chez votre hébergeur DNS. <b>p=none</b> = mode observation, aucun risque
          de blocage de vos emails existants.
        </div>
        <div className="border border-[#DEE2E6] bg-white divide-y divide-[#DEE2E6]">
          {rows.map((r) => (
            <div key={r.testid} className="flex items-center gap-2 p-2">
              <div className="w-24 shrink-0 text-[10px] uppercase tracking-wider font-bold text-[#868E96]">{r.label}</div>
              <code className="flex-1 text-[11px] font-mono break-all" data-testid={`dmarc-${r.testid}`}>{r.value}</code>
              <Button size="sm" variant="outline" className="text-[10px] h-7"
                      data-testid={`dmarc-copy-${r.testid}`} onClick={() => copy(r.value, r.label)}>
                Copier
              </Button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" className="bg-[#E60000] text-white" data-testid="dmarc-copy-full"
                  onClick={() => copy(`${fix.type || "TXT"}\t${fix.name}\t${fix.value}`, "Enregistrement complet")}>
            Copier l'enregistrement DMARC
          </Button>
          <a href="https://www.ionos.fr/assistance/domaines/configurer-des-sous-domaines/creer-un-enregistrement-txt/"
             target="_blank" rel="noreferrer" data-testid="dmarc-ionos-doc">
            <Button size="sm" variant="outline" className="text-xs">
              <ExternalLink className="h-3 w-3 mr-1" />Guide IONOS
            </Button>
          </a>
        </div>
        <ol className="mt-3 text-[11px] text-[#495057] list-decimal pl-4 space-y-0.5" data-testid="dmarc-steps">
          <li>IONOS → <b>Domaines &amp; SSL</b> → cliquez sur votre domaine → <b>DNS</b>.</li>
          <li><b>Ajouter un enregistrement</b> → type <b>TXT</b>.</li>
          <li>Nom / Hostname : <code className="font-mono">{host}</code> (IONOS complète le domaine tout seul).</li>
          <li>Valeur : collez la valeur copiée ci-dessus. TTL : 1 heure.</li>
          <li>Enregistrez, attendez 5 à 30 min, puis cliquez « Revérifier le DNS » ici.</li>
        </ol>
      </div>
    </div>
  );
}

function SchedulerCard({ status, triggering, onTrigger, onRefresh }) {
  if (!status) return null;
  const jobLabels = {
    renewal_reminders: { label: "Rappels de renouvellement", icon: Bell, desc: "Envoie chaque matin à 08:00 UTC aux entreprises dont l'échéance est dans 7 jours." },
    paypal_activation_recheck: { label: "PayPal — recheck activation", icon: Zap, desc: "Toutes les heures — filet de sécurité si un webhook a été manqué." },
  };
  return (
    <div className="border border-[#DEE2E6] bg-white p-6 mt-6" data-testid="scheduler-card">
      <div className="flex items-start gap-3 mb-4 flex-wrap">
        <div className="h-12 w-12 flex items-center justify-center bg-[#0A0A0A] text-white">
          <Clock className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <h2 className="font-display text-xl font-black">Planificateur automatique</h2>
          <p className="text-xs text-[#495057] mt-1">
            Le scheduler interne exécute {status.jobs?.length || 0} job{status.jobs?.length > 1 ? "s" : ""} automatiquement.
            Statut : <span className={`font-bold ${status.running ? "text-[#059669]" : "text-[#E60000]"}`}>{status.running ? "EN COURS" : "ARRÊTÉ"}</span>
          </p>
        </div>
        <button onClick={onRefresh} data-testid="scheduler-refresh" className="text-xs underline text-[#868E96] hover:text-[#0A0A0A]">
          Rafraîchir
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {(status.jobs || []).map((job) => {
          const meta = jobLabels[job.id] || { label: job.id, icon: Clock, desc: "" };
          const Icon = meta.icon;
          return (
            <div key={job.id} className="border border-[#DEE2E6] p-4" data-testid={`scheduler-job-${job.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-[#002FA7]" />
                  <div className="font-display font-bold text-sm">{meta.label}</div>
                </div>
                <button
                  onClick={() => onTrigger(job.id)}
                  disabled={triggering}
                  data-testid={`scheduler-trigger-${job.id}`}
                  className="text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 border border-[#DEE2E6] hover:border-[#0A0A0A] disabled:opacity-50"
                >
                  <Zap className="h-3 w-3 inline mr-1" /> Exécuter
                </button>
              </div>
              <div className="text-xs text-[#495057] mb-2">{meta.desc}</div>
              <div className="text-[10px] font-mono text-[#868E96]">
                {job.next_run ? `Prochain : ${new Date(job.next_run).toLocaleString("fr-FR")}` : "—"}
              </div>
            </div>
          );
        })}
      </div>

      {status.recent_runs?.length > 0 && (
        <div className="mt-4 border-t border-[#DEE2E6] pt-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#495057] mb-2">
            Dernières exécutions ({status.recent_runs.length})
          </div>
          <ul className="space-y-1 text-[11px] font-mono max-h-32 overflow-auto">
            {status.recent_runs.slice(0, 10).map((r, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${r.ok ? "bg-[#059669]" : "bg-[#E60000]"}`} />
                <span className="text-[#868E96]">{new Date(r.started_at).toLocaleString("fr-FR")}</span>
                <span className="font-semibold">{r.job_id}</span>
                {r.result && <span className="text-[#495057]">· {r.result.sent ?? "—"} sent · {r.result.errors ?? 0} err</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}



function StatBadge({ label, value, color }) {
  return (
    <div className="flex-1">
      <div className="text-[10px] uppercase tracking-wider text-[#868E96] font-bold">{label}</div>
      <div className="font-display text-2xl font-black" style={{ color }}>{value ?? 0}</div>
    </div>
  );
}

function DomainStatusCard({ status, loading, onRefresh }) {  if (loading && !status) {
    return (
      <div className="border border-[#DEE2E6] bg-white p-6 flex items-center gap-3 text-sm text-[#868E96]" data-testid="domain-status-loading">
        <RefreshCw className="h-4 w-4 animate-spin" /> Vérification du domaine expéditeur…
      </div>
    );
  }
  if (!status) return null;

  const matched = status.matched_domain;
  const statusValue = matched?.status || (status.is_sandbox ? "sandbox" : "not_added");
  const config = {
    verified: { icon: ShieldCheck, color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", label: "VÉRIFIÉ" },
    pending: { icon: ShieldAlert, color: "#D97706", bg: "#FFFBEB", border: "#FCD34D", label: "EN ATTENTE" },
    sandbox: { icon: ShieldAlert, color: "#D97706", bg: "#FFFBEB", border: "#FCD34D", label: "SANDBOX" },
    failure: { icon: ShieldX, color: "#E60000", bg: "#FEF2F2", border: "#FCA5A5", label: "ÉCHEC" },
    not_added: { icon: ShieldX, color: "#E60000", bg: "#FEF2F2", border: "#FCA5A5", label: "ABSENT" },
    temporary_failure: { icon: ShieldX, color: "#D97706", bg: "#FFFBEB", border: "#FCD34D", label: "ÉCHEC TEMP." },
    not_started: { icon: ShieldAlert, color: "#D97706", bg: "#FFFBEB", border: "#FCD34D", label: "NON DÉMARRÉ" },
  };
  const c = config[statusValue] || config.not_added;
  const Icon = c.icon;

  return (
    <div className="border p-6" style={{ backgroundColor: c.bg, borderColor: c.border }} data-testid="domain-status-card">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-12 w-12 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "white", border: `2px solid ${c.color}` }}>
          <Icon className="h-6 w-6" style={{ color: c.color }} />
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-0.5" style={{ backgroundColor: c.color, color: "white" }} data-testid="domain-status-label">
              {c.label}
            </span>
            <span className="font-mono text-sm font-semibold" data-testid="domain-sender-email">{status.sender_email}</span>
          </div>
          <p className="text-sm" style={{ color: c.color }} data-testid="domain-status-message">
            {status.recommendation || "Impossible d'obtenir le statut du domaine."}
          </p>
          {status.domains && status.domains.length > 0 && (
            <div className="mt-3 text-xs" data-testid="domain-list">
              <div className="font-semibold uppercase tracking-[0.15em] text-[#495057] mb-1">Domaines Resend ({status.domains.length})</div>
              <ul className="space-y-0.5">
                {status.domains.map((d) => (
                  <li key={d.id} className="font-mono flex items-center gap-2">
                    <span className={`inline-block h-1.5 w-1.5 rounded-full`} style={{ backgroundColor: d.status === "verified" ? "#059669" : d.status === "pending" ? "#D97706" : "#E60000" }} />
                    <span>{d.name}</span>
                    <span className="text-[#868E96]">· {d.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <Button onClick={onRefresh} disabled={loading} variant="outline" size="sm" data-testid="domain-status-refresh" className="text-xs">
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${loading ? "animate-spin" : ""}`} /> Vérifier
          </Button>
          <a href="https://resend.com/domains" target="_blank" rel="noreferrer" data-testid="domain-open-resend" className="inline-flex items-center h-9 px-3 border border-[#DEE2E6] bg-white text-xs font-semibold hover:border-[#0A0A0A] transition-colors">
            <ExternalLink className="h-3.5 w-3.5 mr-2" /> Dashboard Resend
          </a>
        </div>
      </div>
    </div>
  );
}
